import { useMemo } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { leerMisAlimentos } from '@/db/alimentos';
import type { Dish, Food } from '@/db/types';
import { formatearNumero } from '@/domain/numbers';
import { formatearKcal } from '@/domain/nutricion';
import { calcularPlatillo, crearResolver, type ResolverIngrediente } from '@/domain/platillos';
import { useAlimentosBase, type EstadoBase } from '@/lib/useAlimentosBase';

/**
 * Los ingredientes de los platillos como están hoy: la base incluida y Mis
 * alimentos. `resolver` es null mientras cargan o si la base no abre.
 */
export function useResolverIngredientes(): {
  base: EstadoBase;
  propios: Food[] | undefined;
  resolver: ResolverIngrediente | null;
} {
  const base = useAlimentosBase();
  const propios = useLiveQuery(() => leerMisAlimentos(), []);
  const resolver = useMemo(
    () =>
      base.estado === 'lista' && propios !== undefined
        ? crearResolver(base.modulo.alimentoBase, new Map(propios.map((f) => [f.id, f])))
        : null,
    [base, propios],
  );
  return { base, propios, resolver };
}

/** "1 ración", "2 raciones", "2.5 raciones" */
export function textoRaciones(raciones: number): string {
  return `${formatearNumero(raciones, { decimales: 2, fijos: false })} ${raciones === 1 ? 'ración' : 'raciones'}`;
}

/** "281 kcal por ración", o el aviso si algún ingrediente no se puede calcular. */
export function kcalPorRacion(platillo: Dish, resolver: ResolverIngrediente): string {
  const { porRacion } = calcularPlatillo(platillo, resolver);
  return porRacion === null ? 'Revisa sus ingredientes' : `${formatearKcal(porRacion.kcal)} por ración`;
}
